import { useState } from 'react';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import ReportSummaryCards from '../shared/ReportSummaryCards';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import { itemAnalysisColumns, ItemAnalysisRow } from './columns';

function formatItemList(
  items: { item_name?: string; quantity_purchased?: number; highest_cost?: number; lowest_cost?: number }[] | undefined,
  valueKey: 'quantity_purchased' | 'highest_cost' | 'lowest_cost' = 'quantity_purchased',
) {
  if (!items?.length) return '—';
  return items
    .slice(0, 3)
    .map((i) => {
      const val = i[valueKey];
      return val != null ? `${i.item_name} (${val})` : String(i.item_name);
    })
    .join(', ');
}

export default function ItemPurchaseAnalysisPage() {
  const dateRangeHook = useReportDateRange('This Month');
  const { from_date, to_date } = dateRangeHook;
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [item, setItem] = useState('');

  const params = {
    from_date,
    to_date,
    supplier: supplier || undefined,
    item: item || undefined,
  };

  const { rows, total, page, setPage, pageSize, summary, isLoading } = usePurchaseReport({
    queryKey: 'itemPurchaseAnalysis',
    params,
    fetcher: (p) => reportsApi.getItemPurchaseAnalysis(p),
  });

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getItemPurchaseAnalysis, params);
    downloadCsv(
      `item-purchase-analysis-${from_date}-${to_date}.csv`,
      columnsToCsvHeaders(itemAnalysisColumns),
      rowsToCsvData(data.rows as unknown as ItemAnalysisRow[], itemAnalysisColumns),
    );
  };

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <PurchaseReportFilters
          dateRangeHook={dateRangeHook}
          supplier={supplier}
          supplierLabel={supplierLabel}
          onSupplierChange={(id, label) => {
            setSupplier(id);
            setSupplierLabel(label);
          }}
          item={item}
          onItemChange={setItem}
          showItem
        />
      }
      summary={
        <div className="space-y-3">
          <ReportSummaryCards
            items={[
              {
                label: 'Top Purchased',
                value: formatItemList(summary.top_purchased_items as never[]),
              },
              {
                label: 'Least Purchased',
                value: formatItemList(summary.least_purchased_items as never[]),
              },
              {
                label: 'Most Expensive',
                value: formatItemList(summary.most_expensive_items as never[], 'highest_cost'),
              },
              {
                label: 'Lowest Cost',
                value: formatItemList(summary.lowest_cost_items as never[], 'lowest_cost'),
              },
            ]}
            columns={2}
          />
        </div>
      }
    >
      <ReportDataTable data={rows as never[]} columns={itemAnalysisColumns} />
    </ReportLayout>
  );
}
